import { useState } from 'react';
import { RotateCcw } from 'lucide-react';

export default function WorklistRow({
    item,
    rowIndex,
    cols = [],
    slots = {},
    statuses = [],
    terminalStatuses = [],
    onEdit,
    onOpen,
    isColEditable,
    assigneePool = [],
    sysCols = new Set(),
    customCols = [],
    selectionMode,
    selected,
    isActive,
    onToggleSelect,
    onRevert,
    revertConfirmId,
    setRevertConfirmId
}) {
    const [drafts, setDrafts] = useState({});
    const resolved = item.resolved || {};
    const isPending  = item.writeback_status === 'pending';
    const isAdminRole = cols?.[0]?._isAdminRole ?? false;
    // Status is platform-managed — check __platform_status__ for terminal lock
    const currentPlatformStatus = resolved.__platform_status__ || item.field_overrides?.__platform_status__;
    const isLocked   = !isAdminRole && terminalStatuses?.includes(currentPlatformStatus);
    const liveVal = (key) => drafts[key] !== undefined ? drafts[key] : (resolved[key] ?? '');
    const stop = e => e.stopPropagation();  // Prevent row click (drawer open) when interacting with cells
    const commit = (key, val) => {
        setDrafts(d => { const n = { ...d }; delete n[key]; return n; });
        if (String(val) !== String(resolved[key] ?? '')) onEdit(item.id, { [key]: val });
    };

    const isFieldOverridden = (key) => item.field_overrides && item.field_overrides[key] !== undefined;

    const getCellCls = (key) => {
        const base = 'text-xs px-2 py-1.5 rounded-lg border bg-white dark:bg-slate-900 dark:text-slate-100 outline-none transition-all w-full';
        if (isFieldOverridden(key)) {
            return `${base} border-amber-400 bg-amber-50/20 dark:bg-amber-955/10 focus:border-amber-500 focus:ring-2 focus:ring-amber-500/15 shadow-sm`;
        }
        return `${base} border-gray-200 dark:border-slate-700 focus:border-indigo-400 focus:ring-2 focus:ring-indigo-500/10`;
    };

    const getCcCls = (key) => {
        const base = 'text-xs px-2 py-1.5 rounded-lg border bg-white dark:bg-slate-900 outline-none transition-all w-full text-amber-700 dark:text-amber-300';
        if (isFieldOverridden(key)) {
            return `${base} border-amber-400 bg-amber-50/20 dark:bg-amber-955/10 focus:border-amber-500 focus:ring-2 focus:ring-amber-500/15`;
        }
        return `${base} border-amber-200 dark:border-amber-800/50 focus:border-amber-400 focus:ring-2 focus:ring-amber-500/10`;
    };

    const isConfirmingRevert = revertConfirmId === item.id;
    const isEvenRow = rowIndex % 2 === 0;

    const stickyBg = selected
        ? 'bg-violet-50 dark:bg-violet-955/40'
        : isActive
            ? 'bg-indigo-50 dark:bg-indigo-955/35'
            : isPending
                ? 'bg-amber-50 dark:bg-amber-955/20'
                : isEvenRow
                    ? 'bg-white dark:bg-slate-900'
                    : 'bg-gray-50 dark:bg-slate-900/50';

    const stickyCls = `sticky z-10 ${stickyBg} group-hover:bg-indigo-50/80 dark:group-hover:bg-indigo-955/30 border-l border-gray-150 dark:border-slate-800 shadow-[-2px_0_5px_rgba(0,0,0,0.02)] transition-colors`;

    return (
        <tr className={`border-b border-gray-100/80 dark:border-slate-800/40 transition-all duration-150 group cursor-pointer
            ${selected
                ? 'bg-violet-50/80 dark:bg-violet-955/25 shadow-[inset_3px_0_0_0_rgb(139,92,246)]'
                : isActive
                    ? 'bg-indigo-50/60 dark:bg-indigo-955/20 shadow-[inset_3px_0_0_0_rgb(99,102,241)]'
                    : isPending
                        ? 'bg-amber-50/30 dark:bg-amber-955/10 shadow-[inset_3px_0_0_0_rgb(245,158,11)]'
                        : isEvenRow
                            ? 'bg-white dark:bg-slate-900'
                            : 'bg-gray-50/60 dark:bg-slate-900/50'
            }
            hover:bg-indigo-50/40 dark:hover:bg-indigo-955/15 hover:shadow-[0_1px_8px_rgba(99,102,241,0.06)]`}
            onClick={selectionMode ? onToggleSelect : onOpen}>
            {/* Checkbox cell */}
            {selectionMode && (
                <td className="pl-5 pr-2 py-2.5" onClick={e => { e.stopPropagation(); onToggleSelect(); }}>
                    <input type="checkbox" checked={!!selected} onChange={onToggleSelect} onClick={e => e.stopPropagation()}
                        className="w-4 h-4 rounded accent-violet-600 cursor-pointer" />
                </td>
            )}
            {cols.map(col => {
                const raw = resolved[col.key];
                const editable = !isLocked && isColEditable(col);
                const isOverridden = isFieldOverridden(col.key);

                // Helper for rendering styled read-only content
                const renderReadOnlyVal = (val, type, isOverriden) => {
                    if (val === undefined || val === null || val === '') {
                        return <span className="text-gray-300 dark:text-slate-600">—</span>;
                    }
                    const strVal = String(val);
                    let content = strVal;
                    if (type === 'currency') {
                        const num = parseFloat(val);
                        if (!isNaN(num)) {
                            content = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(num);
                        }
                    } else if (type === 'percentage') {
                        const num = parseFloat(val);
                        if (!isNaN(num)) {
                            content = strVal.includes('%') ? strVal : `${strVal}%`;
                        }
                    } else if (type === 'url') {
                        const href = strVal.startsWith('http') ? strVal : `https://${strVal}`;
                        content = (
                            <a href={href} target="_blank" rel="noopener noreferrer" className="text-indigo-600 dark:text-indigo-400 hover:underline" onClick={e => e.stopPropagation()}>
                                {strVal}
                            </a>
                        );
                    } else if (type === 'email') {
                        content = (
                            <a href={`mailto:${strVal}`} className="text-indigo-600 dark:text-indigo-400 hover:underline" onClick={e => e.stopPropagation()}>
                                {strVal}
                            </a>
                        );
                    } else if (type === 'phone') {
                        content = (
                            <a href={`tel:${strVal}`} className="text-indigo-600 dark:text-indigo-400 hover:underline" onClick={e => e.stopPropagation()}>
                                {strVal}
                            </a>
                        );
                    }

                    if (isOverriden) {
                        return (
                            <span className="inline-flex items-center gap-1.5 bg-amber-50 dark:bg-amber-950/40 text-amber-800 dark:text-amber-300 px-2 py-0.5 rounded-md border border-amber-250 dark:border-amber-800/40 font-bold shadow-sm">
                                {content}
                                <span className="w-1.5 h-1.5 rounded-full bg-amber-500 animate-pulse shrink-0" title="Value changed (Pending Sync)" />
                            </span>
                        );
                    }
                    return content;
                };

                // ── Read-only cell ──────────────────────────────────────────
                if (!editable) {
                    return (
                        <td key={col.key} className={`px-4 py-2.5 text-gray-700 dark:text-slate-300 truncate max-w-[180px] text-xs font-medium ${isOverridden ? 'bg-amber-50/10 dark:bg-amber-955/5' : ''}`}>
                            {renderReadOnlyVal(raw, col.type, isOverridden)}
                        </td>
                    );
                }

                // ── Dropdown ────────────────────────────────────────────────
                if (col.type === 'dropdown' || col.type === 'multi') {
                    const opts = (col.options || '').split(',').map(o => o.trim()).filter(Boolean);
                    return (
                        <td key={col.key} className="px-3 py-2" onClick={stop}>
                            <select value={raw || ''}
                                onChange={e => onEdit(item.id, { [col.key]: e.target.value })}
                                className={getCellCls(col.key)}>
                                <option value="">— select —</option>
                                {opts.map(o => <option key={o} value={o}>{o}</option>)}
                            </select>
                        </td>
                    );
                }

                // ── Date / Time / DateTime ──────────────────────────────────
                if (col.type === 'date' || col.type === 'time' || col.type === 'datetime-local') {
                    return (
                        <td key={col.key} className="px-3 py-2" onClick={stop}>
                            <input type={col.type} value={liveVal(col.key)}
                                className={getCellCls(col.key)}
                                onChange={e => setDrafts(d => ({ ...d, [col.key]: e.target.value }))}
                                onBlur={e => commit(col.key, e.target.value)} />
                        </td>
                    );
                }

                // ── Number / Currency / Percentage ──────────────────────────
                if (col.type === 'number' || col.type === 'currency' || col.type === 'percentage') {
                    return (
                        <td key={col.key} className="px-3 py-2" onClick={stop}>
                            <input type="number" step="any" value={liveVal(col.key)}
                                className={getCellCls(col.key)}
                                onChange={e => setDrafts(d => ({ ...d, [col.key]: e.target.value }))}
                                onBlur={e => commit(col.key, e.target.value)} />
                        </td>
                    );
                }

                // ── Checkbox / Boolean ──────────────────────────────────────
                if (col.type === 'checkbox') {
                    return (
                        <td key={col.key} className={`px-4 py-2 text-center ${isOverridden ? 'bg-amber-50/10 dark:bg-amber-955/5 border border-amber-250/20' : ''}`} onClick={stop}>
                            <input type="checkbox" checked={!!raw}
                                className="w-4 h-4 rounded accent-indigo-600 cursor-pointer"
                                onChange={e => onEdit(item.id, { [col.key]: e.target.checked })} />
                        </td>
                    );
                }

                // ── Default Text Box / Url / Email / Phone / Textarea ─────────
                let inputType = 'text';
                if (col.type === 'url') inputType = 'url';
                else if (col.type === 'email') inputType = 'email';
                else if (col.type === 'phone') inputType = 'tel';

                return (
                    <td key={col.key} className="px-3 py-2" onClick={stop}>
                        <input type={inputType} value={liveVal(col.key)}
                            placeholder={col.label}
                            className={getCellCls(col.key)}
                            onChange={e => setDrafts(d => ({ ...d, [col.key]: e.target.value }))}
                            onBlur={e => commit(col.key, e.target.value)} />
                    </td>
                );
            })}
            {/* ── System Audit Columns (Scrollable) ──────────────────────────────────── */}
            {sysCols.has('modified_by') && (
            <td className="px-4 py-2.5 text-xs text-gray-600 dark:text-slate-400 whitespace-nowrap">
                {item.last_modified_by
                    ? <span className="font-medium">{item.last_modified_by}</span>
                    : <span className="text-gray-300 dark:text-slate-600">—</span>}
            </td>
            )}
            {sysCols.has('remarks') && (
            <td className="px-4 py-2.5 text-xs text-gray-500 dark:text-slate-400 max-w-[200px] truncate" title={item.last_remark || ''}>
                {item.last_remark
                    ? <span className="italic">{item.last_remark}</span>
                    : <span className="text-gray-300 dark:text-slate-600">—</span>}
            </td>
            )}
            {sysCols.has('modified_at') && (
            <td className="px-4 py-2.5 text-xs text-gray-500 dark:text-slate-400 whitespace-nowrap font-mono">
                {item.last_modified_at
                    ? new Date(item.last_modified_at).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })
                    : <span className="text-gray-300 dark:text-slate-600">—</span>}
            </td>
            )}
            {/* ── Custom Platform Columns (Scrollable) ── */}
            {(customCols || []).map(cc => {
                const val = item.resolved?.[cc.key] || item.field_overrides?.[cc.key] || '';
                const stop = e => e.stopPropagation();
                const isOverridden = isFieldOverridden(cc.key);

                if (cc.type === 'dropdown') {
                    const opts = (cc.options || '').split(',').map(o => o.trim()).filter(Boolean);
                    return (
                        <td key={cc.key} className="px-3 py-2" onClick={stop}>
                            <select value={val} onChange={e => onEdit(item.id, { [cc.key]: e.target.value })} className={getCcCls(cc.key) + ' cursor-pointer'}>
                                <option value="">—</option>
                                {opts.map(o => <option key={o} value={o}>{o}</option>)}
                            </select>
                        </td>
                    );
                }
                if (cc.type === 'checkbox') {
                    return (
                        <td key={cc.key} className={`px-3 py-2 text-center ${isOverridden ? 'bg-amber-50/10 dark:bg-amber-955/5 border border-amber-250/20' : ''}`} onClick={stop}>
                            <input type="checkbox" checked={val === true || val === 'true'}
                                className="w-4 h-4 rounded accent-amber-600 cursor-pointer"
                                onChange={e => onEdit(item.id, { [cc.key]: e.target.checked })} />
                        </td>
                    );
                }
                if (cc.type === 'date') {
                    return (
                        <td key={cc.key} className="px-3 py-2" onClick={stop}>
                            <input type="date" value={val} className={getCcCls(cc.key)}
                                onChange={e => onEdit(item.id, { [cc.key]: e.target.value })} />
                        </td>
                    );
                }
                // text / number
                return (
                    <td key={cc.key} className="px-3 py-2" onClick={stop}>
                        <input type={cc.type === 'number' ? 'number' : 'text'} value={liveVal(cc.key)} placeholder={cc.label}
                            className={getCcCls(cc.key)}
                            onChange={e => setDrafts(d => ({ ...d, [cc.key]: e.target.value }))}
                            onBlur={e => commit(cc.key, e.target.value)} />
                    </td>
                );
            })}

            {/* ── Platform-managed Status (Sticky far right) ── */}
            {sysCols.has('status') && (() => {
                const statusVal = item.resolved?.__platform_status__ || item.field_overrides?.__platform_status__ || '';
                const statusColor = {
                    'Open':           'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400 border-green-200 dark:border-green-800/40',
                    'In Progress':    'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400 border-amber-200 dark:border-amber-800/40',
                    'Closed':         'bg-gray-100 text-gray-600 dark:bg-slate-700/40 dark:text-slate-400 border-gray-200 dark:border-slate-600/40',
                    'Pending Review': 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400 border-red-200 dark:border-red-800/40',
                };
                const chipClass = statusColor[statusVal] || 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400 border-blue-200 dark:border-blue-800/40';
                const rightPos = sysCols.has('assignee') ? 160 + 180 : 160;
                return (
                    <td style={{ right: `${rightPos}px` }} className={`${stickyCls} px-3 py-2 text-center w-[150px] min-w-[150px] max-w-[150px]`} onClick={stop}>
                        <select
                            value={statusVal}
                            onChange={e => onEdit(item.id, { __platform_status__: e.target.value })}
                            disabled={isLocked}
                            className={`text-[11px] px-2.5 py-1 rounded-full border font-bold outline-none transition-all cursor-pointer
                                appearance-none text-center min-w-[100px]
                                ${isLocked ? 'opacity-55 cursor-not-allowed' : ''}
                                ${statusVal ? chipClass : 'bg-gray-50 text-gray-400 border-gray-200 dark:bg-slate-800 dark:text-slate-500 dark:border-slate-700'}`}>
                            <option value="">— No Status —</option>
                            {(statuses || []).map(s => <option key={s} value={s}>{s}</option>)}
                        </select>
                    </td>
                );
            })()}

            {/* ── Platform-managed Assignee (Sticky far right) ── */}
            {sysCols.has('assignee') && (
            <td style={{ right: '160px' }} className={`${stickyCls} px-3 py-2 w-[180px] min-w-[180px] max-w-[180px]`} onClick={stop}>
                <select
                    value={item.resolved?.__platform_assignee__ || item.field_overrides?.__platform_assignee__ || ''}
                    onChange={e => onEdit(item.id, { __platform_assignee__: e.target.value })}
                    disabled={isLocked}
                    className={`text-xs px-2 py-1.5 rounded-lg border border-emerald-200 dark:border-emerald-800/50 bg-white dark:bg-slate-900
                        dark:text-slate-100 outline-none focus:border-emerald-400 focus:ring-2 focus:ring-emerald-500/10 transition-all w-full
                        text-emerald-700 dark:text-emerald-300
                        ${isLocked ? 'opacity-55 cursor-not-allowed' : ''}`}>
                    <option value="">— Unassigned —</option>
                    {(assigneePool || []).map(u => <option key={u} value={u}>{u}</option>)}
                </select>
            </td>
            )}


            {/* ── Sync State badge (Sticky far right) ── */}
            <td style={{ right: '0px' }} className={`${stickyCls} px-4 py-2.5 w-[160px] min-w-[160px] max-w-[160px]`} onClick={stop}>
                <div className="flex items-center justify-end gap-1.5">
                    {isLocked && <span title="Record locked" className="text-xs text-gray-400">🔒</span>}
                    <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider ${
                        isPending
                            ? 'bg-orange-100 text-orange-600 dark:bg-orange-900/30 dark:text-orange-400'
                            : 'bg-teal-100 text-teal-600 dark:bg-teal-900/30 dark:text-teal-400'
                    }`}>
                        <span className={`w-1.5 h-1.5 rounded-full ${isPending ? 'bg-orange-500 animate-pulse' : 'bg-teal-500'}`} />
                        {isPending ? 'PENDING' : 'SYNCED'}
                    </span>
                    {/* Inline Revert — only when pending */}
                    {isPending && !isConfirmingRevert && (
                        <button
                            title="Discard changes — revert to source"
                            onClick={() => setRevertConfirmId(item.id)}
                            className="opacity-0 group-hover:opacity-100 transition-opacity inline-flex items-center gap-1 px-2 py-1 rounded-lg
                                text-[10px] font-bold text-gray-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20
                                border border-transparent hover:border-red-200 dark:hover:border-red-800/40">
                            Revert
                        </button>
                    )}
                    {/* Inline confirm: replaces button */}
                    {isPending && isConfirmingRevert && (
                        <span className="inline-flex items-center gap-1 animate-in fade-in duration-150">
                            <button onClick={() => onRevert(item.id)}
                                className="px-2 py-0.5 rounded-lg text-[10px] font-bold bg-red-500 text-white hover:bg-red-600 transition-colors">
                                Yes, revert
                            </button>
                            <button onClick={() => setRevertConfirmId(null)}
                                className="px-2 py-0.5 rounded-lg text-[10px] font-bold text-gray-500 hover:text-gray-700 hover:bg-gray-100 dark:hover:bg-slate-800 transition-colors">
                                Cancel
                            </button>
                        </span>
                    )}
                </div>
            </td>
        </tr>
    );
}
